// §6.2 — CSRF middleware. The csrf cookie is non-HttpOnly; we read it ONLY
// here and forward as X-CSRF-Token on state-changing methods.
const CSRF_COOKIE = "quartel_csrf";
let csrfMintInFlight: Promise<string | undefined> | null = null;

export function readCsrfCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]+)`),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

interface CsrfResponse {
  csrfToken?: unknown;
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  return input instanceof Request ? input.method.toUpperCase() : "GET";
}

export async function ensureCsrfToken(
  baseFetch: typeof fetch = fetch,
): Promise<string | undefined> {
  const existing = readCsrfCookie();
  if (existing) return existing;
  return mintCsrfToken(baseFetch);
}

/** Force one shared CSRF mint after a rejected double-submit request.
 * @example `await refreshCsrfToken(fetch)` */
export async function refreshCsrfToken(
  baseFetch: typeof fetch = fetch,
): Promise<string | undefined> {
  return mintCsrfToken(baseFetch);
}

function mintCsrfToken(baseFetch: typeof fetch): Promise<string | undefined> {
  if (csrfMintInFlight) return csrfMintInFlight;
  const pending = requestCsrfToken(baseFetch);
  csrfMintInFlight = pending;
  void pending.finally(() => {
    if (csrfMintInFlight === pending) csrfMintInFlight = null;
  });
  return pending;
}

async function requestCsrfToken(
  baseFetch: typeof fetch,
): Promise<string | undefined> {
  try {
    const res = await baseFetch("/api/v2/auth/csrf", {
      credentials: "include",
    });
    const json = (await res
      .json()
      .catch((): CsrfResponse => ({}))) as CsrfResponse;
    const responseToken =
      typeof json.csrfToken === "string" ? json.csrfToken : undefined;
    // A concurrent response may have rotated the browser cookie after this
    // request started. The double-submit header must follow the current cookie.
    return readCsrfCookie() ?? responseToken;
  } catch {
    return undefined;
  }
}

function mergeHeaders(...items: Array<HeadersInit | undefined>): Headers {
  const headers = new Headers();
  for (const item of items) {
    new Headers(item).forEach((value, key) => headers.set(key, value));
  }
  return headers;
}

function csrfRequest(
  input: Request,
  init: RequestInit | undefined,
  token: string,
): Request {
  const headers = mergeHeaders(input.headers, init?.headers);
  headers.set("X-CSRF-Token", token);
  return new Request(input, { ...init, headers });
}

export function withCsrf(baseFetch: typeof fetch): typeof fetch {
  const wrapped: typeof fetch = async (input, init) => {
    const method = requestMethod(input, init);
    if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
      return baseFetch(input, init);
    }
    const token = await ensureCsrfToken(baseFetch);
    if (!token) {
      return baseFetch(input, init);
    }
    if (input instanceof Request) {
      return baseFetch(csrfRequest(input, init, token));
    }
    const headers = mergeHeaders(init?.headers);
    headers.set("X-CSRF-Token", token);
    return baseFetch(input, { ...init, headers });
  };
  return wrapped;
}
